/**
 * BRAND RENAME PANEL
 *
 * Self-scoped brand rename widget rendered inside the Branding tab of
 * BrandManagePage. Wraps the `renameBrand` service; scoped by the
 * `brandId` prop (which comes from the manage page's ownership-gated
 * brand doc). Only affects the brand the current viewer owns.
 *
 * Not included in v1: merging two brand docs (needs cross-account
 * coordination). Brand owner requests that via admin.
 */

import { useState } from 'react';
import type { Brand } from '../../types/brand';
import { renameBrand } from '../../services/brandRename';
import { PenLine } from 'lucide-react';

interface Props {
  brand: Brand;
  onRenamed?: (newName: string) => void;
}

export default function BrandRenamePanel({ brand, onRenamed }: Props) {
  const [draft, setDraft] = useState(brand.name);
  const [running, setRunning] = useState(false);
  const [feedback, setFeedback] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null);

  const run = async () => {
    const to = draft.trim();
    if (!to) {
      setFeedback({ text: 'Enter a new name first.', kind: 'err' });
      return;
    }
    if (to === brand.name) {
      setFeedback({ text: 'New name is the same as current.', kind: 'err' });
      return;
    }
    if (!window.confirm(
      `Rename brand "${brand.name}" → "${to}"?\n\nThis updates the brand doc AND every collector's items with matching brand. For casing fixes, no polish slugs change. For spelling changes, slugs get rewritten too. Not reversible via the UI.`
    )) return;
    setRunning(true);
    setFeedback(null);
    try {
      const summary = await renameBrand({
        brandId: brand.id,
        fromBrandLower: brand.name.trim().toLowerCase(),
        toBrand: to,
      });
      setFeedback({
        text: `Renamed. ${summary.itemsUpdated} item${summary.itemsUpdated === 1 ? '' : 's'} across the community were updated.`,
        kind: 'ok',
      });
      onRenamed?.(to);
    } catch (err) {
      setFeedback({ text: `Rename failed: ${(err as Error).message}`, kind: 'err' });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="glass-card p-6">
      <h2 className="text-lg font-semibold text-glass mb-1 font-body inline-flex items-center gap-2">
        <PenLine className="w-4 h-4 text-glass-secondary" />
        Brand Name
      </h2>
      <p className="text-sm text-glass-secondary mb-4 font-body">
        Correct the canonical spelling or casing of your brand. Changes
        propagate to every collector's item + the polish catalog. Note:
        merging two brand docs (e.g. duplicates) is admin-only for now.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 items-start">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={running}
          className="flex-1 bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
          placeholder={brand.name}
        />
        <button
          onClick={run}
          disabled={running || !draft.trim() || draft.trim() === brand.name}
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 font-body"
        >
          {running ? 'Renaming…' : 'Rename brand'}
        </button>
      </div>

      {feedback && (
        <div
          className={`mt-3 text-sm font-body px-3 py-2 rounded ${
            feedback.kind === 'ok'
              ? 'bg-emerald-500/10 text-emerald-300'
              : 'bg-red-500/10 text-red-300'
          }`}
        >
          {feedback.text}
        </div>
      )}
    </div>
  );
}
